import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { XMarkIcon, EyeIcon, DocumentTextIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../../ui/LoadingSpinner';
import { chartOfAccountsService } from '../../../services/accounting/chart-of-accounts';
import { formatCurrency, formatDate } from '../../../utils/formatting';

/**
 * AccountAnalysisModal Component
 * 
 * Modal for displaying detailed account analysis including:
 * - Transaction history
 * - Balance trends
 * - Usage statistics
 * - Account details
 */
const AccountAnalysisModal = ({ isOpen, account, onClose }) => {
    const { t } = useTranslation();
    const { currentCompany } = useAuth();
    const [loading, setLoading] = useState(false);
    const [analysis, setAnalysis] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [dateRange, setDateRange] = useState({
        startDate: '',
        endDate: ''
    });

    const loadAccountAnalysis = useCallback(async () => {
        setLoading(true);
        try {
            const analysisData = await chartOfAccountsService.getAccountAnalysis(
                currentCompany.id,
                account.id,
                dateRange.startDate || null,
                dateRange.endDate || null
            );
            setAnalysis(analysisData);
        } catch (error) {
            console.error('Error loading account analysis:', error);
        } finally {
            setLoading(false);
        }
    }, [currentCompany?.id, account?.id, dateRange]);

    // Load account analysis when modal opens
    useEffect(() => {
        if (isOpen && account && currentCompany?.id) {
            loadAccountAnalysis();
        }
    }, [isOpen, account, currentCompany?.id, loadAccountAnalysis]);

    const handleDateRangeChange = (field, value) => {
        setDateRange(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const renderOverviewTab = () => {
        if (!analysis) return null;

        return (
            <div className="space-y-6">
                {/* Account Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="card">
                        <div className="card-body text-center">
                            <div className="text-2xl font-bold text-green-600">
                                {formatCurrency(analysis.totalDebits)}
                            </div>
                            <div className="text-sm text-gray-500">{t('coa.total_debits', 'Total Debits')}</div>
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-body text-center">
                            <div className="text-2xl font-bold text-red-600">
                                {formatCurrency(analysis.totalCredits)}
                            </div>
                            <div className="text-sm text-gray-500">{t('coa.total_credits', 'Total Credits')}</div>
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-body text-center">
                            <div className="text-2xl font-bold text-blue-600">
                                {formatCurrency(Math.abs(analysis.netMovement))}
                            </div>
                            <div className="text-sm text-gray-500">
                                {t('coa.net_movement', 'Net Movement')}
                                {analysis.netMovement >= 0 ? ' (Dr)' : ' (Cr)'}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Statistics */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="card">
                        <div className="card-body">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">
                                {t('coa.transaction_statistics', 'Transaction Statistics')}
                            </h3>
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">{t('coa.transaction_count', 'Transaction Count')}:</span>
                                    <span className="font-medium">{analysis.transactionCount}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">{t('coa.average_transaction', 'Average Transaction')}:</span>
                                    <span className="font-medium">{formatCurrency(analysis.averageTransactionSize)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">{t('coa.current_balance', 'Current Balance')}:</span>
                                    <span className="font-medium">{formatCurrency(account.currentBalance || 0)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">{t('coa.opening_balance', 'Opening Balance')}:</span>
                                    <span className="font-medium">{formatCurrency(account.openingBalance || 0)}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-body">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">
                                {t('coa.account_details', 'Account Details')}
                            </h3>
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">{t('coa.account_type', 'Type')}:</span>
                                    <span className="font-medium capitalize">{account.accountType}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">{t('coa.category', 'Category')}:</span>
                                    <span className="font-medium">{account.category}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">{t('coa.normal_balance', 'Normal Balance')}:</span>
                                    <span className="font-medium capitalize">{account.normalBalance}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">{t('coa.status', t('common.status'))}:</span>
                                    <span className={`font-medium ${account.isActive ? 'text-green-600' : 'text-red-600'}`}>
                                        {account.isActive ? t('coa.active', t('common.active')) : t('coa.inactive', t('common.inactive'))}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Period Information */}
                {analysis.period.startDate && analysis.period.endDate && (
                    <div className="card">
                        <div className="card-body">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">
                                {t('coa.analysis_period', 'Analysis Period')}
                            </h3>
                            <div className="flex items-center justify-between">
                                <span className="text-gray-600">
                                    {formatDate(analysis.period.startDate)} - {formatDate(analysis.period.endDate)}
                                </span>
                                <span className="text-sm text-gray-500">
                                    {analysis.transactionCount} {t('coa.transactions', 'transactions')}
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderTransactionsTab = () => {
        if (!analysis || !analysis.transactions) return null;

        return (
            <div className="space-y-4">
                {/* Date Range Filter */}
                <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('coa.start_date', 'Start Date')}
                        </label>
                        <input
                            type="date"
                            value={dateRange.startDate}
                            onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                            className="input input-bordered input-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('coa.end_date', 'End Date')}
                        </label>
                        <input
                            type="date"
                            value={dateRange.endDate}
                            onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                            className="input input-bordered input-sm"
                        />
                    </div>
                    <div className="pt-6">
                        <button
                            onClick={() => setDateRange({ startDate: '', endDate: '' })}
                            className="btn btn-outline btn-sm"
                        >
                            {t('common.clear', 'Clear')}
                        </button>
                    </div>
                </div>

                {/* Transactions Table */}
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('coa.date', t('common.date'))}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('coa.description', t('common.description'))}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('coa.reference', 'Reference')}
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('coa.debit', 'Debit')}
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('coa.credit', 'Credit')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {analysis.transactions.map((transaction, index) => (
                                <tr key={index} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatDate(transaction.date)}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900">
                                        {transaction.description}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {transaction.reference || '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                                        {transaction.debit > 0 ? (
                                            <span className="text-green-600 font-medium">
                                                {formatCurrency(transaction.debit)}
                                            </span>
                                        ) : '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                                        {transaction.credit > 0 ? (
                                            <span className="text-red-600 font-medium">
                                                {formatCurrency(transaction.credit)}
                                            </span>
                                        ) : '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {analysis.transactions.length === 0 && (
                    <div className="text-center py-8">
                        <DocumentTextIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                        <p className="text-gray-500">
                            {t('coa.no_transactions', 'No transactions found for the selected period')}
                        </p>
                    </div>
                )}
            </div>
        );
    };

    if (!isOpen || !account) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
                    <div className="flex items-center space-x-3">
                        <EyeIcon className="w-6 h-6 text-blue-600" />
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900">
                                {t('coa.account_analysis', 'Account Analysis')}
                            </h2>
                            <p className="text-sm text-gray-500">
                                {account.accountCode} - {account.accountName}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>

                {/* Tabs */}
                <div className="border-b border-gray-200 flex-shrink-0">
                    <nav className="flex space-x-8 px-6">
                        <button
                            onClick={() => setActiveTab('overview')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'overview'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            <ChartBarIcon className="w-4 h-4 inline mr-2" />
                            {t('coa.overview', 'Overview')}
                        </button>
                        <button
                            onClick={() => setActiveTab('transactions')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'transactions'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            <DocumentTextIcon className="w-4 h-4 inline mr-2" />
                            {t('coa.transactions', 'Transactions')}
                        </button>
                    </nav>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <LoadingSpinner size="lg" />
                            <span className="ml-3 text-gray-600">{t('common.loading', t('common.loading'))}</span>
                        </div>
                    ) : (
                        <>
                            {activeTab === 'overview' && renderOverviewTab()}
                            {activeTab === 'transactions' && renderTransactionsTab()}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 flex-shrink-0 bg-white">
                    <button
                        onClick={onClose}
                        className="btn btn-secondary"
                    >
                        {t('common.close', t('common.close'))}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AccountAnalysisModal;